import { supabase } from "../../../lib/supabase";

/**
 * Dispatch Q&A — Ship 1 API helpers.
 *
 * The portal is the system of record. SMS bridge (Ship 2) reads/writes
 * the same `dispatch_questions` rows. Notifications fire via DB triggers
 * (notify_on_dispatch_question, notify_on_dispatch_question_reply).
 */

export type DispatchPromptTemplate =
  | "confirm_meeting"
  | "ho_confirmed"
  | "color_confirmed"
  | "build_on_schedule"
  | "custom";

export interface DispatchQuestion {
  id: string;
  deal_id: string;
  asked_by: string;
  /** Legacy single-recipient column. Kept in sync with asked_to_ids[0] by a
   *  Postgres BEFORE trigger so edge functions/RLS that still read it work.
   *  New code should read asked_to_ids. */
  asked_to: string;
  /** Canonical recipient list (May 1, 2026 migration). One question can be
   *  asked of multiple people; the first reply closes it for everyone. */
  asked_to_ids: string[];
  prompt_template: DispatchPromptTemplate | null;
  body: string;
  status: "open" | "answered" | "cancelled";
  answer_body: string | null;
  answered_by: string | null;
  created_at: string;
  answered_at: string | null;
  acknowledged_by_asker_at: string | null;
  bridge_sms_sent_at: string | null;
  bridge_sms_acknowledged_at: string | null;
  bridge_sms_reminded_at: string | null;
}

/** Resolve the recipient list off a question row, preferring the new array
 *  column but falling back to the legacy single-recipient column for any
 *  rows written before the May 1, 2026 migration ran. */
export function recipientsOf(q: DispatchQuestion): string[] {
  if (q.asked_to_ids && q.asked_to_ids.length > 0) return q.asked_to_ids;
  return q.asked_to ? [q.asked_to] : [];
}

/** Quick-prompt presets — tap-to-fill. Tunable based on use. */
export const QUICK_PROMPTS: Array<{ key: DispatchPromptTemplate; label: string; bodyTemplate: (homeowner: string) => string }> = [
  { key: "confirm_meeting", label: "Confirm meeting?", bodyTemplate: (h) => `Confirm the upcoming meeting for ${h}?` },
  { key: "ho_confirmed", label: "HO confirmed?", bodyTemplate: (h) => `Has ${h} confirmed availability?` },
  { key: "color_confirmed", label: "Color confirmed?", bodyTemplate: (h) => `Did ${h} confirm shingle/drip-edge color?` },
  { key: "build_on_schedule", label: "Build on schedule?", bodyTemplate: (h) => `Is the build for ${h} still on schedule?` },
];

/** Pull the open + recent answered questions for a deal — for the pill badge + thread view. */
export async function listQuestionsForDeal(dealId: string) {
  const { data, error } = await supabase
    .from("dispatch_questions")
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as DispatchQuestion[];
}

/** Pull all open questions assigned to a user — for the Notifications "Dispatch Q&A" filter when we need to show context. Uses array-contains on asked_to_ids; the legacy asked_to column is kept in sync by trigger so this finds both new and old rows. */
export async function listOpenQuestionsForUser(userId: string) {
  const { data, error } = await supabase
    .from("dispatch_questions")
    .select("*")
    .contains("asked_to_ids", [userId])
    .eq("status", "open")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as DispatchQuestion[];
}

export async function askQuestion(params: {
  dealId: string;
  askedBy: string;
  /** One or more recipients. The first reply from any recipient closes the
   *  question for everyone. Must be non-empty. */
  askedTo: string[];
  promptTemplate: DispatchPromptTemplate | null;
  body: string;
}) {
  if (!params.askedTo || params.askedTo.length === 0) {
    throw new Error("askQuestion requires at least one recipient");
  }
  // Dedupe while preserving order (first listed becomes the legacy asked_to).
  const seen = new Set<string>();
  const askedToIds = params.askedTo.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  const { data, error } = await supabase
    .from("dispatch_questions")
    .insert({
      deal_id: params.dealId,
      asked_by: params.askedBy,
      // Both columns set explicitly — the BEFORE trigger will overwrite
      // asked_to to match asked_to_ids[0], but writing it here too means
      // the row is consistent even if the trigger hasn't been deployed yet.
      asked_to: askedToIds[0],
      asked_to_ids: askedToIds,
      prompt_template: params.promptTemplate,
      body: params.body,
      status: "open",
    })
    .select()
    .single();
  if (error) throw error;
  return data as DispatchQuestion;
}

export async function answerQuestion(params: {
  questionId: string;
  answeredBy: string;
  answerBody: string;
}) {
  const { error } = await supabase
    .from("dispatch_questions")
    .update({
      status: "answered",
      answer_body: params.answerBody,
      answered_by: params.answeredBy,
      answered_at: new Date().toISOString(),
    })
    .eq("id", params.questionId);
  if (error) throw error;
}

export async function acknowledgeAnswer(questionId: string) {
  const { error } = await supabase
    .from("dispatch_questions")
    .update({ acknowledged_by_asker_at: new Date().toISOString() })
    .eq("id", questionId);
  if (error) throw error;
}

export async function cancelQuestion(questionId: string) {
  const { error } = await supabase
    .from("dispatch_questions")
    .update({ status: "cancelled" })
    .eq("id", questionId);
  if (error) throw error;
}

/** Urgency tier for the badge — drives color. */
export type QuestionUrgency = "fresh" | "warning" | "overdue" | "answered";

export function urgencyForQuestion(q: DispatchQuestion, now = new Date()): QuestionUrgency {
  if (q.status === "answered") return "answered";
  const ageHrs = (now.getTime() - new Date(q.created_at).getTime()) / 3_600_000;
  if (ageHrs >= 24) return "overdue";
  if (ageHrs >= 4) return "warning";
  return "fresh";
}

/** Default routing logic: who should this question go to for this deal +
 *  prompt? Returns an array so the modal can pre-check multiple chips. */
export function defaultAskedTo(
  deal: { owner_id: string | null; split_rm_id: string | null; pre_inspection_assigned_to: string | null; phase: string | null },
  promptTemplate: DispatchPromptTemplate | null,
): string[] {
  const out: string[] = [];
  // PRE/adjuster confirmation → preselect the assigned RS if set; otherwise
  // owner. Owner is always implicit unless they're already the RS.
  if (promptTemplate === "confirm_meeting" && deal.pre_inspection_assigned_to) {
    out.push(deal.pre_inspection_assigned_to);
    if (deal.owner_id && deal.owner_id !== deal.pre_inspection_assigned_to) {
      out.push(deal.owner_id);
    }
    return out;
  }
  if (deal.owner_id) out.push(deal.owner_id);
  return out;
}
